import client from './client';
import type {
  GitHubRepo,
  WorkflowRun,
  PullRequest,
  GitHubStats,
  PaginatedResponse,
} from '@/types';

export interface RepoListParams {
  search?: string;
  language?: string;
  is_archived?: boolean;
  page?: number;
  page_size?: number;
}

export interface WorkflowRunParams {
  status?: string;
  conclusion?: string;
  date_from?: string;
  date_to?: string;
  branch?: string;
  event?: string;
  actor_login?: string;
  page?: number;
  page_size?: number;
}

export interface BuildsStatsParams {
  date_from?: string;
  date_to?: string;
}

export interface BranchBuildStats {
  branch: string;
  total_builds: number;
  success_count: number;
  failure_count: number;
  other_count: number;
}

export interface BranchPRStats {
  branch: string;
  merges: number;
  comments: number;
  reviews: number;
}

export interface BuildsStats {
  total_builds: number;
  total_repos: number;
  success_count: number;
  failure_count: number;
  other_count: number;
  success_rate_pct: number;
  branch_build_stats: BranchBuildStats[];
  branch_pr_stats: BranchPRStats[];
}

export interface PullRequestParams {
  state?: string;
  author?: string;
  page?: number;
  page_size?: number;
}

export async function fetchRepositories(params?: RepoListParams) {
  const { data } = await client.get<PaginatedResponse<GitHubRepo>>('/github/repositories', { params });
  return data;
}

export async function fetchRepository(repoId: string) {
  const { data } = await client.get<GitHubRepo>(`/github/repositories/${repoId}`);
  return data;
}

export async function fetchRepoWorkflowRuns(repoId: string, params?: WorkflowRunParams) {
  const { data } = await client.get<PaginatedResponse<WorkflowRun>>(
    `/github/repositories/${repoId}/workflow-runs`,
    { params },
  );
  return data;
}

export async function fetchRepoPullRequests(repoId: string, params?: PullRequestParams) {
  const { data } = await client.get<PaginatedResponse<PullRequest>>(
    `/github/repositories/${repoId}/pull-requests`,
    { params },
  );
  return data;
}

export async function fetchAllWorkflowRuns(params?: WorkflowRunParams) {
  const { data } = await client.get<PaginatedResponse<WorkflowRun>>('/github/workflow-runs', { params });
  return data;
}

export async function fetchAllPullRequests(params?: PullRequestParams) {
  const { data } = await client.get<PaginatedResponse<PullRequest>>('/github/pull-requests', { params });
  return data;
}

export async function fetchGitHubStats() {
  const { data } = await client.get<GitHubStats>('/github/stats');
  return data;
}

export async function fetchBuildsStats(params?: BuildsStatsParams) {
  const { data } = await client.get<BuildsStats>('/github/builds-stats', { params });
  return data;
}

export interface CollectorStatus {
  collector_name: string;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_error: string | null;
  items_synced: number;
  next_sync_at: string | null;
  is_healthy: boolean;
  schedule: string | null;
}

export async function fetchGitHubCollectorStatus() {
  const { data } = await client.get<CollectorStatus>('/collectors/github');
  return data;
}

// ── Engineering Analytics ─────────────────────────────────────────────────────

export interface AnalyticsParams {
  days?: number;
}

export interface AnalyticsSummary {
  total_prs_period: number;
  merged_prs_period: number;
  hotfixes_period: number;
  zero_review_merges_period: number;
  failed_builds_period: number;
  force_merges_period: number;
  period_days: number;
}

export interface PRAuthorStat {
  author_login: string;
  total_prs: number;
  merged_prs: number;
  open_prs: number;
  avg_pr_size: number;
}

export interface DailyPRCount {
  date: string;
  author_login: string;
  count: number;
}

export interface PRActivityResponse {
  by_author: PRAuthorStat[];
  daily: DailyPRCount[];
  period_days: number;
}

export interface AnalyticsPR {
  id: string;
  github_pr_number: number;
  title: string;
  author_login: string | null;
  repo_name: string;
  head_branch: string | null;
  base_branch: string | null;
  merged_at: string | null;
  pr_created_at: string;
  html_url: string | null;
}

export interface BuildFailureStat {
  actor_login: string;
  total_runs: number;
  failures: number;
  failure_rate: number;
}

export async function fetchAnalyticsSummary(params?: AnalyticsParams) {
  const { data } = await client.get<AnalyticsSummary>('/github/analytics/summary', { params });
  return data;
}

export async function fetchPRActivity(params?: AnalyticsParams) {
  const { data } = await client.get<PRActivityResponse>('/github/analytics/pr-activity', { params });
  return data;
}

export async function fetchHotfixes(params?: AnalyticsParams) {
  const { data } = await client.get<AnalyticsPR[]>('/github/analytics/hotfixes', { params });
  return data;
}

export async function fetchZeroReviews(params?: AnalyticsParams) {
  const { data } = await client.get<AnalyticsPR[]>('/github/analytics/zero-reviews', { params });
  return data;
}

export async function fetchBuildFailures(params?: AnalyticsParams) {
  const { data } = await client.get<BuildFailureStat[]>('/github/analytics/build-failures', { params });
  return data;
}

export async function fetchForceMerges(params?: AnalyticsParams) {
  const { data } = await client.get<AnalyticsPR[]>('/github/analytics/force-merges', { params });
  return data;
}
